import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import StickyBar from './StickyBar';
import { KeyFeatures } from './KeyFeatures';
import { EvaluationSteps } from './EvaluationSteps';
import { ProsCons } from './ProsCons';
import { TradingRules } from './TradingRules';
import { TrustpilotReviews } from './TrustpilotReviews';
import { RelatedPosts } from './RelatedPosts';

interface ReviewLayoutProps {
  firm: any;
  details?: any;
  reviewsData?: any;
}

// Updated schema helper to include aggregateRating
function getReviewStructuredData(firm: any) {
  const reviews = firm?.trustpilot?.reviews || [];
  if (reviews.length === 0) return null;

  const ratingSum = reviews.reduce((sum: number, r: any) => sum + (r.rating || 0), 0);
  const averageRating = (ratingSum / reviews.length).toFixed(1);

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": firm.name,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": reviews.length.toString()
    },
    "review": reviews.map((r: any) => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating,
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": r.name || r.source || "Anonymous"
      },
      "reviewBody": r.description || r.content || ""
    }))
  };
}

export const ReviewLayout = ({ firm, details, reviewsData }: ReviewLayoutProps) => {
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {firm.trustpilot?.reviews?.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(getReviewStructuredData(firm))}
          </script>
        </Helmet>
      )}

      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 md:py-20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6 text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-black rounded-xl p-2 flex items-center justify-center shadow-md">
              <img
                src={`/assets/logos/${firm.id}/logo.svg`}
                alt={firm.name}
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-yellow-400">
            Is {firm.name} The Best Prop Firm For You?
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-yellow-100 max-w-2xl mx-auto mt-4">
            Honest review of {firm.name} — payouts, trading rules, real trader feedback & exclusive promo deals you won’t find anywhere else.
          </p>

          <a
            href={`/go/${firm.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center mt-6 bg-yellow-400 text-black font-semibold px-6 py-3 rounded-full hover:bg-yellow-300 transition"
          >
            🔥 Claim Discount & Get Funded
          </a>

          <p className="text-sm text-yellow-300 mt-4">
            Trusted by 24,000+ traders | Save up to ${firm.evaluation?.discount?.discountedPrice
              ? (firm.evaluation.fee - firm.evaluation.discount.discountedPrice).toFixed(2)
              : 'big'} today | Instant Signup
          </p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: showStickyBar ? 1 : 0, y: showStickyBar ? 0 : -20 }}
        transition={{ duration: 0.3 }}
      >
        <StickyBar firm={firm} />
      </motion.div>

      <div className="bg-gray-950 text-yellow-100 py-12 sm:py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

          <KeyFeatures firm={firm} />

          {firm.evaluation?.steps?.length > 0 && (
            <EvaluationSteps evaluation={firm.evaluation} />
          )}

          <ProsCons
            pros={details?.pros?.length > 0 ? details.pros : ['Will be added soon...']}
            cons={details?.cons?.length > 0 ? details.cons : ['Will be added soon...']}
          />

          {details?.rules?.length > 0 && (
            <TradingRules rules={details.rules} />
          )}

          {firm.trustpilot?.reviews?.length > 0 ? (
            <TrustpilotReviews
              reviews={firm.trustpilot.reviews.map((r: any) => ({
                content: r.description || r.content || '',
                source: r.name,
                rating: r.rating,
              }))}
              trustpilotUrl={firm.trustpilot.url}
            />
          ) : (
            <div className="bg-red-900/40 p-6 rounded-xl text-center text-yellow-400">
              Dev team will fix this section soon.
            </div>
          )}

          {reviewsData && reviewsData.length > 0 && (
            <RelatedPosts posts={firm.relatedPosts || []} />
          )}
        </div>
      </div>
    </>
  );
};

export default ReviewLayout;
